/**
 * YouTube video IDs are always 11 characters from the URL-safe base64 alphabet
 * (letters, digits, `-`, `_`).  Anchoring the pattern keeps random 11-char
 * substrings from accidentally matching.
 */
const VIDEO_ID = /^[A-Za-z0-9_-]{11}$/;

/**
 * Extract the canonical 11-character YouTube video ID from any of the public
 * URL formats an instructor is likely to paste.  Returns `null` when the input
 * is not a recognised YouTube URL (or `null`/empty/whitespace).
 *
 * Accepted shapes:
 *   - https://www.youtube.com/watch?v=ID[&extra=…]
 *   - https://youtu.be/ID[?si=…]
 *   - https://www.youtube.com/shorts/ID[?…]
 *   - https://www.youtube.com/embed/ID[?…]
 *   - https://www.youtube-nocookie.com/embed/ID[?…]
 *
 * The check is host-anchored so a non-YouTube domain containing the same path
 * segments cannot match.
 */
export function parseYouTubeId(input: string | null | undefined): string | null {
    if (!input) return null;
    const trimmed = input.trim();
    if (!trimmed) return null;

    let url: URL;
    try {
        url = new URL(trimmed);
    } catch {
        return null;
    }

    const host = url.hostname.toLowerCase().replace(/^www\./, '');

    if (host === 'youtu.be') {
        const id = url.pathname.slice(1).split('/')[0];
        return VIDEO_ID.test(id) ? id : null;
    }

    if (host === 'youtube.com' || host === 'm.youtube.com') {
        if (url.pathname === '/watch') {
            const id = url.searchParams.get('v') ?? '';
            return VIDEO_ID.test(id) ? id : null;
        }
        const pathMatch = url.pathname.match(/^\/(?:shorts|embed)\/([^/?#]+)/);
        if (pathMatch && VIDEO_ID.test(pathMatch[1])) return pathMatch[1];
    }

    if (host === 'youtube-nocookie.com') {
        const pathMatch = url.pathname.match(/^\/embed\/([^/?#]+)/);
        if (pathMatch && VIDEO_ID.test(pathMatch[1])) return pathMatch[1];
    }

    return null;
}

/**
 * Build the canonical privacy-enhanced embed URL we persist for a given video
 * ID.  Centralised here so the parser and the iframe builder agree on shape.
 */
export function toEmbedUrl(videoId: string): string {
    return `https://www.youtube-nocookie.com/embed/${videoId}`;
}

/**
 * Named YouTube thumbnails, largest first.  `hqdefault` is the last resort:
 * every public video has one, while `maxresdefault` (1280×720) and
 * `sddefault` (640×480) are only generated for some uploads.
 */
const THUMBNAIL_QUALITIES = ['maxresdefault', 'sddefault', 'hqdefault'] as const;

/**
 * Thumbnail URLs for a video, from highest resolution to `hqdefault`.
 * A missing size responds 404, or 200 with a 120×90 placeholder.  Callers
 * that render the image should walk this list until one is a real frame.
 */
export function thumbnailUrlCandidates(videoId: string): readonly string[] {
    return THUMBNAIL_QUALITIES.map(
        (quality) => `https://img.youtube.com/vi/${videoId}/${quality}.jpg`,
    );
}

/**
 * Preferred thumbnail URL: the first entry of `thumbnailUrlCandidates`.
 * This is the URL we hand to callers; the image element is responsible for
 * falling back when YouTube did not generate that size.
 */
export function toThumbnailUrl(videoId: string): string {
    return thumbnailUrlCandidates(videoId)[0];
}
